import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-components';
import { connect } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { LayerHost } from '@fluentui/react';
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';
import { SpecialtyCreateCommand, SpecialtyUpdateCommand } from '../../../models/Entities/Specialties/SpecialtyModels';
import { SnomedItem } from '../../../models/Entities/Snomed/SnomedModels';
import SnomedSelector from '../../../commons/component/Inputs/SnomedSelector/SnomedSelector.Component';
import * as ownFunctions from './MedicalSpecialtiesForm.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const MedicalSpecialtiesFormComponent = (props: ownFunctions.Props) => {
  const [submitted, setSubmitted] = useState(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();
  const [name, setName] = useState<string>('');
  const [snomedCode, setSnomedCode] = useState<string>('');
  const [snomedDisplayName, setSnomedDisplayName] = useState<string>('');
  const [snomedName, setSnomedName] = useState<string>('');
  const [t] = useTranslation();

  useEffect(() => {
    if (props.specialty) {
      setName(props.specialty.name || '');
      setSnomedCode(props.specialty.snomedCode || '');
      setSnomedDisplayName(props.specialty.snomedName || props.specialty.snomedCode || '');
      setSnomedName(props.specialty.snomedName || '');
    } else {
      resetForm();
    }
  }, [props.specialty]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      props.GetAllSpecialties();
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setName('');
    setSnomedCode('');
    setSnomedDisplayName('');
    setSnomedName('');
    setSubmitted(false);
    setValidation(undefined);
  };

  const validate = (): boolean => {
    const errors: string[] = [];
    if (!name.trim()) {
      errors.push(t('medicalSpecialties.form.validation.name', { defaultValue: 'El nombre es obligatorio' }));
    }
    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    if (props.specialty) {
      const payload: SpecialtyUpdateCommand = {
        id: props.specialty.id,
        name: name.trim(),
        snomedCode: snomedCode.trim() || null,
        snomedName: snomedName.trim() || null
      };
      props.UpdateSpecialty(payload);
    } else {
      const payload: SpecialtyCreateCommand = {
        name: name.trim(),
        snomedCode: snomedCode.trim() || null,
        snomedName: snomedName.trim() || null
      };
      props.CreateSpecialty(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  return (
    <>
    <Drawer
      open={props.isOpen}
      type="overlay"
      position="end"
      size="small"
      onOpenChange={(_, data) => {
        if (!data.open) handleCancel();
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          {t('medicalSpecialties.form.title', { defaultValue: 'Especialidad médica' })}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {submitted && validation && !validation.success && (
          <MessageBar intent="error" layout="multiline" style={{ marginBottom: 12 }}>
            <MessageBarBody>
              <MessageBarTitle>
                {t('medicalSpecialties.form.review', { defaultValue: 'Revise la información ingresada' })}
              </MessageBarTitle>
              <ul>
                {validation.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}

        {submitted && (props.failAdding || props.failUpdating) && props.error && (
          <ErrorMessageComponent Error={props.error} />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field label={t('medicalSpecialties.form.snomed', { defaultValue: 'Código SNOMED' })}>
            <SnomedSelector
              value={snomedCode}
              displayText={snomedDisplayName}
              layerHostId="snomed-callout-layer-host"
              onSelect={(item: SnomedItem) => {
                setSnomedCode(item.id);
                setSnomedDisplayName(item.name);
                setSnomedName(item.name);
                setName(item.name);
              }}
              onClear={() => {
                setSnomedCode('');
                setSnomedDisplayName('');
                setSnomedName('');
              }}
            />
          </Field>

          <Field label={t('medicalSpecialties.form.snomedName', { defaultValue: 'Nombre SNOMED' })}>
            <Input value={snomedName} disabled />
          </Field>

          <Field
            label={t('medicalSpecialties.form.name', { defaultValue: 'Nombre' })}
            required
            validationMessage={
              submitted && !name.trim()
                ? t('medicalSpecialties.form.validation.name', { defaultValue: 'El nombre es obligatorio' })
                : undefined
            }
            validationState={submitted && !name.trim() ? 'error' : undefined}
          >
            <Input value={name} onChange={(_, data) => setName(data.value || '')} />
          </Field>
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', width: '100%' }}>
          <Button appearance="secondary" onClick={handleCancel}>
            <Trans i18nKey={'medicalSpecialties.form.footer.cancel'} defaultValue="Cancelar" />
          </Button>
          <Button appearance="primary" onClick={handleSave} disabled={!name.trim() || !snomedCode.trim() || !snomedName.trim() || props.isAdding || props.isUpdating}>
            <Trans i18nKey={'medicalSpecialties.form.footer.save'} defaultValue="Guardar" />
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
    {props.isOpen && createPortal(
      <LayerHost id="snomed-callout-layer-host" style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999999 }} />,
      document.body
    )}
  </>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(MedicalSpecialtiesFormComponent as any);
